import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function ProfilePat() {
  const [user, setUser] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    photo_profil: '',
    institution: '',
    num_caisse: ''
  });
  const [newPhoto, setNewPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const API_BASE = config.apiBase;

  // Normalisation des URLs des photos de profil
  const normalizeProfilePhoto = (url) => config.normalizeProfilePhoto(url);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    const photo = normalizeProfilePhoto(stored.photo_profil || '');
    setUser({
      nom: stored.nom || '',
      prenom: stored.prenom || '',
      email: stored.email || '',
      telephone: stored.telephone || '',
      photo_profil: photo,
      institution: stored.institution || '',
      num_caisse: stored.num_caisse || ''
    });
    setPhotoPreview(photo || null);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 2 Mo');
        return;
      }
      setNewPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const dataToSend = {
        nom: user.nom,
        prenom: user.prenom,
        telephone: user.telephone
      };

      if (newPhoto) {
        const base64 = await convertToBase64(newPhoto);
        dataToSend.photo_profil_base64 = base64;
      }

      const response = await fetch(`${API_BASE}/pat/modifier_profil.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Profil mis à jour');
        const updatedPhoto = normalizeProfilePhoto(data.user.photo_profil);
        const updatedUser = { ...data.user, photo_profil: updatedPhoto };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setPhotoPreview(updatedPhoto);
        setNewPhoto(null);
      } else {
        toast.error(data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/auth/change_password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Mot de passe modifié');
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-primary mb-6">👤 Mon profil PAT</h1>

      {/* Photo de profil */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Photo de profil</h2>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {photoPreview ? (
            <img src={photoPreview} alt="Profil" className="w-24 h-24 rounded-full object-cover border-2 border-blue-500" />
          ) : (
            <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {user.prenom?.[0]}{user.nom?.[0]}
            </div>
          )}
          <div>
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleFileChange} 
              className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
            />
            <p className="text-xs text-gray-400 mt-2">JPG, PNG (max 2 Mo)</p>
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Informations personnelles</h2>
        <form onSubmit={handleUpdateProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input 
              type="text" 
              value={user.nom} 
              onChange={(e) => setUser({ ...user, nom: e.target.value })} 
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
            <input 
              type="text" 
              value={user.prenom} 
              onChange={(e) => setUser({ ...user, prenom: e.target.value })} 
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input 
              type="email" 
              value={user.email} 
              disabled 
              className="w-full border rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input 
              type="tel" 
              value={user.telephone} 
              onChange={(e) => setUser({ ...user, telephone: e.target.value })} 
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
            <input 
              type="text" 
              value={user.institution} 
              disabled 
              className="w-full border rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de caisse</label>
            <input 
              type="text" 
              value={user.num_caisse} 
              disabled 
              className="w-full border rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed" 
            />
          </div>
          <div className="md:col-span-2">
            <button 
              type="submit" 
              disabled={loading} 
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour le profil'}
            </button>
          </div>
        </form>
      </div>

      {/* Changer mot de passe */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold mb-4">🔒 Changer le mot de passe</h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ancien mot de passe</label>
            <input 
              type="password" 
              value={passwordData.old_password} 
              onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })} 
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
            <input 
              type="password" 
              value={passwordData.new_password} 
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} 
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
            <input 
              type="password" 
              value={passwordData.confirm_password} 
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} 
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required 
            />
          </div>
          <div>
            <button 
              type="submit" 
              disabled={loading} 
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              Changer le mot de passe
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}